/**
 * The sidebar (boss rule: sub-menus): groups from the route table. Modules still to come
 * (W2–W5) show with a "Soon" pill and open a placeholder that says which sprint brings them.
 * Desktop: collapsible to icons (remembered). Phones: an off-canvas drawer from the top bar.
 */
import { ChevronDown, ChevronsLeft, ChevronsRight, Download } from 'lucide-react';
import { useEffect, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { cx } from '../../components/ui';
import { BRAND } from '../../config/brand';
import { useAuth } from '../../lib/auth';
import { useInstallPrompt } from '../../lib/pwa';
import { GROUPS, NAV_ORDER, ROUTES, routeFor, type GroupId, type RouteDef } from '../routes';
import { Wordmark } from './Wordmark';

const OPEN_KEY = 'wms.nav.open';
const isGroup = (x: string): x is GroupId => GROUPS.some((g) => g.id === x);

const itemCls = (active: boolean) =>
  cx('group relative flex min-h-touch items-center gap-3 rounded-tag px-3 text-[15px] transition-colors', active ? 'bg-rail-hover font-semibold text-rail-ink' : 'text-rail-muted hover:bg-rail-hover/70 hover:text-rail-ink');

/** The active marker is the catalogue card's red rule, turned upright. */
const ActiveRule = () => <span aria-hidden className="absolute bottom-2.5 left-0 top-2.5 w-[3px] rounded-r bg-rule" />;

export function Sidebar({ collapsed, onToggleCollapse, onNavigate }: { collapsed: boolean; onToggleCollapse?: () => void; onNavigate?: () => void }) {
  const { can } = useAuth();
  const { pathname } = useLocation();
  const { canInstall, install } = useInstallPrompt();
  const visible = ROUTES.filter((r) => r.nav && can(...r.perm));
  const activeGroup = routeFor(pathname)?.nav;
  const [open, setOpen] = useState<string[]>(() => {
    try {
      return JSON.parse(localStorage.getItem(OPEN_KEY) ?? '["parcels","setup"]') as string[];
    } catch {
      return ['parcels', 'setup'];
    }
  });
  useEffect(() => {
    if (activeGroup && activeGroup !== 'top' && !open.includes(activeGroup)) setOpen((o) => [...o, activeGroup]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeGroup]);
  const toggle = (g: string) => {
    const next = open.includes(g) ? open.filter((x) => x !== g) : [...open, g];
    setOpen(next);
    try {
      localStorage.setItem(OPEN_KEY, JSON.stringify(next));
    } catch {
      /* ignore */
    }
  };

  const leaf = (r: RouteDef, nested: boolean) => (
    <NavLink key={r.path} to={r.path} end={r.end || r.path === '/'} onClick={onNavigate} title={collapsed ? r.title : undefined} className={({ isActive }) => cx(itemCls(isActive), nested && !collapsed && 'pl-11 text-sm')}>
      {({ isActive }) => (
        <>
          {isActive && <ActiveRule />}
          {(!nested || collapsed) && <r.icon aria-hidden className="h-[18px] w-[18px] shrink-0" />}
          {!collapsed && <span className="truncate">{r.title}</span>}
          {r.coming && !collapsed && <span className="ml-auto rounded-full border border-rail-line px-1.5 text-[10px] font-semibold uppercase tracking-wide text-rail-muted">Soon</span>}
        </>
      )}
    </NavLink>
  );

  const groupHeader = (id: string, label: string, Icon: RouteDef['icon'], isOpen: boolean, hasActive: boolean) => (
    <button type="button" aria-expanded={isOpen} onClick={() => toggle(id)} className={cx('flex min-h-touch w-full items-center gap-3 rounded-tag px-3 text-[15px] transition-colors hover:bg-rail-hover/70', hasActive ? 'text-rail-ink' : 'text-rail-muted hover:text-rail-ink')}>
      <Icon aria-hidden className="h-[18px] w-[18px] shrink-0" />
      <span className="flex-1 text-left">{label}</span>
      <ChevronDown aria-hidden className={cx('h-4 w-4 transition-transform', isOpen ? 'rotate-0' : '-rotate-90')} />
    </button>
  );

  return (
    <nav aria-label="Main" className="flex h-full flex-col border-r border-rail-line bg-rail text-rail-ink">
      <div className={cx('flex h-16 shrink-0 items-center border-b border-rail-line', collapsed ? 'justify-center px-2' : 'px-4')}>
        <Wordmark compact={collapsed} />
      </div>
      <div className="scrollbar-thin flex-1 space-y-0.5 overflow-y-auto p-2">
        {NAV_ORDER.map((x) => {
          if (!isGroup(x)) {
            const r = visible.find((v) => v.path === x && v.nav === 'top');
            return r ? leaf(r, false) : null;
          }
          const g = GROUPS.find((gg) => gg.id === x)!;
          const kids = visible.filter((r) => r.nav === x);
          if (!kids.length) return null;
          const isOpen = open.includes(x) || collapsed;
          return (
            <div key={x} className={cx(collapsed && 'border-t border-rail-line pt-1')}>
              {!collapsed && groupHeader(x, g.label, g.icon, isOpen, activeGroup === x)}
              {isOpen && <div className="space-y-0.5">{kids.map((r) => leaf(r, true))}</div>}
            </div>
          );
        })}
      </div>
      <div className="space-y-0.5 border-t border-rail-line p-2">
        {visible.find((r) => r.path === '/help') && leaf(visible.find((r) => r.path === '/help')!, false)}
        {canInstall && (
          <button type="button" onClick={() => void install()} title={collapsed ? 'Install app' : undefined} className={itemCls(false) + ' w-full'}>
            <Download aria-hidden className="h-[18px] w-[18px] shrink-0" />
            {!collapsed && 'Install app'}
          </button>
        )}
        {onToggleCollapse && (
          <button type="button" onClick={onToggleCollapse} aria-label={collapsed ? 'Expand menu' : 'Collapse menu'} className="hidden min-h-touch w-full items-center gap-3 rounded-tag px-3 text-sm text-rail-muted hover:bg-rail-hover/70 hover:text-rail-ink lg:flex">
            {collapsed ? <ChevronsRight aria-hidden className="h-[18px] w-[18px]" /> : <ChevronsLeft aria-hidden className="h-[18px] w-[18px]" />}
            {!collapsed && 'Collapse'}
          </button>
        )}
        {!collapsed && <p className="px-3 pb-1 pt-2 text-xs text-rail-muted">{BRAND.operator}</p>}
      </div>
    </nav>
  );
}
